import { useEffect, useCallback, useMemo, useState } from "react"
import { useGridApiRef } from "@mui/x-data-grid-premium"
import { debounce } from "lodash"
import { useContextPersistence } from "./use-context-persistence"
import { useEventSubscriptions } from "./use-event-subscriptions"

/**
 * DataGrid persistence using context preferences
 *
 * @param {string} gridId - Unique identifier for this grid instance
 * @param {Object} options - Configuration options
 * @param {boolean} options.isPersistenceEnabled - Whether persistence is enabled (default: true)
 * @param {number} options.debounceMs - Debounce time for saving state (default: 500ms)
 * @param {Function} options.onColumnOrderChange - Function to call when column order changes (updates persistence)
 *
 * @returns {Object} Persistence utilities and state
 */
export function useDataGridPersistence(gridId, options = {}) {
  const {
    isPersistenceEnabled = true,
    debounceMs = 500,
    onColumnOrderChange,
  } = options

  const apiRef = useGridApiRef()

  const { isContextAvailable, gridState: contextGridState, saveGridStateToContext } = useContextPersistence(gridId)

  const [isStateLoaded, setIsStateLoaded] = useState(!!contextGridState)
  const [initialState, setInitialState] = useState(contextGridState)

  // Mark as loaded when context becomes available
  useEffect(() => {
    if (contextGridState !== null) {
      setInitialState(contextGridState)
      setIsStateLoaded(true)
    } else if (isPersistenceEnabled) {
      setIsStateLoaded(true)
    }
  }, [contextGridState, isPersistenceEnabled])

  const getEnhancedState = useCallback((gridState, customStates = {}) => {
    return {
      ...gridState,
      ...customStates,
    }
  }, [])

  const saveState = useCallback((customStates = {}) => {
    if (!isPersistenceEnabled || !gridId || !apiRef.current || !isContextAvailable) {
      return false
    }

    const currentState = apiRef.current.exportState()
    const mergedCustomStates = {
      customSort: currentState?.customSort || initialState?.customSort || null,
      customColumnOrder: currentState?.customColumnOrder || initialState?.customColumnOrder || null,
      ...customStates,
    }

    const enhancedState = getEnhancedState(currentState, mergedCustomStates)
    const saveResult = saveGridStateToContext(enhancedState)

    if (saveResult) {
      setInitialState(enhancedState)
    }

    return saveResult
  }, [isPersistenceEnabled, gridId, apiRef, getEnhancedState, isContextAvailable, saveGridStateToContext, initialState])

  const handleSortChange = useCallback((sortField, sortDirection) => {
    if (!isPersistenceEnabled || !apiRef.current || !isContextAvailable) return

    const customSort = sortField && sortDirection ? { field: sortField, direction: sortDirection } : null
    return saveState({ customSort })
  }, [isPersistenceEnabled, apiRef, isContextAvailable, saveState])

  const applyCustomColumnOrder = useCallback(() => {
    if (!isPersistenceEnabled || !apiRef.current || !isContextAvailable) return

    const currentState = apiRef.current.exportState()
    const customColumnOrder = currentState.columns?.orderedFields || null

    saveState({ customColumnOrder })

    if (onColumnOrderChange) {
      onColumnOrderChange("custom")
    }
  }, [isPersistenceEnabled, apiRef, isContextAvailable, saveState, onColumnOrderChange])

  const debouncedSave = useMemo(
    () => debounce(() => { saveState() }, debounceMs),
    [saveState, debounceMs])

  const cleanup = useCallback(() => {
    debouncedSave.cancel()
  }, [debouncedSave])

  // Set up event subscriptions for auto-save
  useEventSubscriptions(apiRef, {
    enabled: isPersistenceEnabled,
    isStateLoaded,
    saveState: debouncedSave,
    applyCustomColumnOrder,
  })

  // Cleanup on unmount
  useEffect(() => {
    return cleanup
  }, [cleanup])

  const savedCustomSort = initialState?.customSort || null
  const savedCustomColumnOrder = initialState?.customColumnOrder || null

  return {
    apiRef,
    initialState,
    isStateLoaded,
    savedCustomSort,
    savedCustomColumnOrder,
    saveState,
    handleSortChange,
  }
}

export { useDataGridPersistence as default }
